// @ts-nocheck
import {EditOutlined, InfoCircleOutlined, SendOutlined} from "@ant-design/icons";
import {blue} from "@ant-design/colors";
import {message, Tooltip} from "antd";
import ButtonGroup from "antd/lib/button/button-group";
import React, {FC, memo, useEffect, useRef, useState} from "react";
import {Link, useHistory, useLocation} from "react-router-dom";
import {TableType} from "../../containers/bookings/BookingList/BookingListColumns";
import ReverseBookingModal from "../../containers/bookings/BookingList/ReverseBookingModal";
import {GetCoursesQuery} from "../../generated/graphql";
import i18n from "../../services/i18n";
import {QueryItem} from "../../types";
import Button from "../Button/Button";
import DeleteButton from "../DeleteButton/DeleteButton";
import MoreMenu from "../../containers/bookings/BookingList/MoreMenu/MoreMenu";
import css from "./ActionButtonGroup.less";
import Roles from "../../../shared/models/roles";
import BookingHistoryDrawer from "../../containers/bookingHistory/BookingHistoryDrawer";
import {LocationState} from "../../helpers/returnBackHelper";

type GetCoursesQueryItem = QueryItem<GetCoursesQuery["courses"]>;
type GetBookinsQueryItem = QueryItem<TableType>;

// export type TableType = GetBookinsQuery["bookings"]["items"][0] & ExtraColumns;

type DeleteMessageParams = {
    loading: boolean | undefined;
    data: Record<string, unknown> | undefined;
    error: Error | undefined;
};

export const deleteMessageStateFromApolloQueryResult = ({loading, data, error}: DeleteMessageParams) =>
    (Boolean(error) && "error") || (Boolean(data) && "success") || (loading && "loading") || "initial";

type UseDeleteMessageConfig = {
    state: "loading" | "error" | "success" | "initial";
    loadingMessage: string;
    successMessage: string;
    errorMessage: string;
};

let deleteMessageSeq = 0;

export const useDeleteMessage = ({state, loadingMessage, successMessage, errorMessage}: UseDeleteMessageConfig) => {
    // Eigener Schlüssel je Löschvorgang: rc-notification 4.6 stürzt ab („reading 'props'“), wenn derselbe
    // Schlüssel neu angelegt wird, während die vorige Meldung noch ausblendet (zweimal kurz hintereinander löschen).
    const run = useRef({state: "initial", key: ""});

    useEffect(() => {
        if (state === "initial") {
            return;
        }
        // Neuer Vorgang nur beim Übergang nach „loading“; ein neuer Text während „loading“ aktualisiert dieselbe Meldung.
        if ((state === "loading" && run.current.state !== "loading") || !run.current.key) {
            deleteMessageSeq += 1;
            run.current.key = `deleting-${deleteMessageSeq}`;
        }
        run.current.state = state;
        const key = run.current.key;

        if (state === "loading") {
            message.loading({content: loadingMessage, key}, 0);
        }

        if (state === "success") {
            message.success({content: successMessage, key});
        }

        if (state === "error") {
            message.error({content: errorMessage, key});
        }
    }, [state, loadingMessage, successMessage, errorMessage]);

    // Verschwindet die Zeile, bevor das Löschen antwortet, bliebe die Lademeldung (Dauer 0) sonst stehen.
    useEffect(
        () => () => {
            if (run.current.state === "loading") {
                message.destroy(run.current.key);
            }
        },
        [],
    );
};

type CustomIconProps = "edit" | "send";
export type InitiatedBy = "Booker" | "Attendee" | "Booking" | "Course" | "CourseType" | "CourseCategory" | "Location" | "Staff";

type ActionButtonGroupProps = {
    editUrl?: string;
    customIcon?: CustomIconProps;
    deleteDisabled?: boolean;
    deleteConfirmText?: React.ReactNode;
    deleteConfirmDisabled?: boolean;
    deleteDisabledText?: string;
    deleteOnConfirm?: () => void;
    /** Löschen gar nicht anbieten (z. B. Sichtbarkeitsregel „Buchungsaktionen: Nein“, #1188). */
    deleteHidden?: boolean;
    deleteTestId?: string;
    editTestId?: string;
    /** Gesetzt: Bearbeiten ist gesperrt, der Text erklärt als Tooltip warum (z. B. nach DSGVO-Löschantrag). */
    editDisabledText?: string;
    detailsUrl?: string;
    detailsState: GetCoursesQueryItem | GetBookinsQueryItem;
    editParams?: GetBookinsQueryItem;
    detailsTestId?: string;
    isSettled?: boolean;
    moreMenuItems?: MenuProps["items"];
    highestRole?: Roles;
    initiatedBy?: InitiatedBy;
};

const ActionButtonGroup: FC<ActionButtonGroupProps> = ({
    deleteTestId = "DeleteButton",
    editTestId = "EditButton",
    detailsTestId = "DetailsButton",
    editUrl = "/",
    editDisabledText,
    customIcon,
    deleteDisabled = false,
    deleteConfirmText = "confirm",
    deleteConfirmDisabled = false,
    deleteDisabledText = "disabled",
    deleteHidden = false,
    deleteOnConfirm = () => {
        console.log("confirmed");
    },
    detailsUrl = "/",
    detailsState,
    editParams,
    isSettled,
    moreMenuItems,
    highestRole,
    initiatedBy,
}) => {
    const [reverseBookingModalVisible, setReverseBookingModalVisible] = useState(false);

    const history = useHistory();
    const location = useLocation();

    // Intelligente returnTo-Preservation für verschachtelte Navigation
    const currentState = location.state as LocationState | undefined;
    const preservedReturnTo = currentState?.returnTo ?? currentState?.from?.pathname;

    const getCustomIcon = (customIcon?: CustomIconProps) => {
        switch (customIcon) {
            case "edit":
                return <EditOutlined style={{color: "rgb(7, 96, 255)"}} />;
                break;
            case "send":
                return <SendOutlined style={{color: "rgb(7, 96, 255)"}} />;
            default:
                return <EditOutlined style={{color: "rgb(7, 96, 255)"}} />;
                break;
        }
    };

    if (isSettled) {
        return (
            <>
                <ReverseBookingModal open={reverseBookingModalVisible} closeModal={() => setReverseBookingModalVisible(false)} refetch={refetch} />
                <ButtonGroup className={css.root}>
                    <Button data-testid={editTestId} size="small" type="danger" style={{border: "1px solid", borderColor: blue[5]}}>
                        <Link
                            to={{
                                pathname: editUrl,
                                // editParams,
                                state: {
                                    ...editParams,
                                    from: {pathname: history.location.pathname + history.location.search},
                                },
                            }}
                        >
                            {i18n.containers.bookings.BookingList.reverseBooking()}
                        </Link>
                    </Button>
                    <BookingHistoryDrawer bookingId={detailsState.id} buttonProps={{size: "small"}} buttonStyle={{border: `1px solid ${blue[5]}`, color: blue[5], fontSize: "1.05em"}} />
                </ButtonGroup>
            </>
        );
    }

    return (
        <ButtonGroup className={css.root}>
            {highestRole !== Roles.Instructor && editDisabledText && (
                <Tooltip title={editDisabledText}>
                    {/* span: deaktivierte Buttons lösen keine Maus-Events für den Tooltip aus */}
                    <span>
                        <Button data-testid={editTestId} size="small" disabled style={{border: "1px solid", borderColor: blue[5]}}>
                            <EditOutlined />
                        </Button>
                    </span>
                </Tooltip>
            )}
            {highestRole !== Roles.Instructor &&
                !editDisabledText &&
                (editParams ? (
                    <Button data-testid={editTestId} size="small" style={{border: "1px solid", borderColor: blue[5]}}>
                        <Link
                            to={{
                                pathname: editUrl,
                                state: {
                                    ...editParams,
                                    immediateReturnTo: history.location.pathname + history.location.search,
                                    returnTo: preservedReturnTo,
                                },
                            }}
                        >
                            <EditOutlined style={{color: "rgb(7, 96, 255)"}} />
                        </Link>
                    </Button>
                ) : (
                    <Button data-testid={editTestId} size="small" href={editUrl}>
                        {getCustomIcon(customIcon)}
                        {/* <EditOutlined style={{color: "rgb(7, 96, 255)"}} /> */}
                    </Button>
                ))}
            {highestRole !== Roles.Instructor && !deleteHidden && (
                <DeleteButton
                    data-testid={deleteTestId}
                    size="small"
                    confirmText={deleteConfirmText}
                    confirmDisabled={deleteConfirmDisabled}
                    disabled={deleteDisabled}
                    onConfirm={deleteOnConfirm}
                    disabledText={deleteDisabledText}
                    style={{border: "1px solid", borderColor: blue[5]}}
                />
            )}
            {detailsUrl === "/" ? null : (
                <Button data-testid={detailsTestId} size="small">
                    <Link
                        to={{
                            pathname: detailsUrl,
                            state: {
                                ...detailsState,
                                immediateReturnTo: history.location.pathname + history.location.search,
                                returnTo: preservedReturnTo,
                            },
                        }}
                    >
                        <InfoCircleOutlined style={{color: "rgb(7, 96, 255)"}} />
                    </Link>
                </Button>
            )}
            {initiatedBy === "Booking" && (
                <BookingHistoryDrawer bookingId={detailsState.id} buttonProps={{size: "small"}} buttonStyle={{border: `1px solid ${blue[5]}`, color: blue[5], fontSize: "1.05em"}} />
            )}
            {moreMenuItems && <MoreMenu moreMenuItems={moreMenuItems} />}
        </ButtonGroup>
    );
};

export default memo(ActionButtonGroup);
